import React, { MutableRefObject, Ref, RefObject } from 'react';

import { Property } from 'csstype';
import { BridgeComponent } from 'mfe-react-bridge';

import { Trans } from '/utils/translations';

/*
 * Article JSON Shapes
 *
 * These types correspond directly to the input json
 *
 */

export type DateJson = {
  display: string;
  last_modified: string;
  published: string;
};

export type EditorJson = {
  email?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  biography?: string;
  job_title?: string;
  is_analyst?: boolean;
  id?: string;
  image_url?: string;
};

export type BlockElementJson = {
  type?: string;
  hidden: boolean;
  content?: string;
};

export type HTMLContentJson = BlockElementJson & {
  type: 'html';
  content: string;
};

export type ContentContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    content?: BlockElementJson;
    source?: BlockElementJson;
    prefix?: BlockElementJson;
    title?: BlockElementJson;
  };
  columnNumber?: number;
  numbered?: boolean;
  name: 'content';
};

export type AssetContentJson = {
  asset: string;
  name: string;
  icon: string;
  type: 'asset';
  hidden: boolean;
};

export type AssetContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    asset: AssetContentJson;
    image?: ImageContentJson;
  };
  name: 'asset' | 'media';
  background?: BlockBackgroundsType;
  onClick?: CallableFunction;
};

export type ImageContentJson = {
  alt: string;
  description: string;
  gnpd_id: string;
  hidden: boolean;
  link: string;
  source?: string;
  type: 'image' | 'svg'; // Note: svg type is not actually a SVG but a PNG due to CMS conversion of SVG to PNG
};

export type SocialMediaPlatformType =
  | 'facebook'
  | 'instagram'
  | 'linkedin'
  | 'pinterest'
  | 'tiktok'
  | 'twitter'
  | 'youtube';

export type SocialMediaContentJson = {
  alt: string;
  gnpd_id?: string;
  hidden: boolean;
  link: string;
  provider: SocialMediaPlatformType;
  type: 'social-media'; // The type of this media element
  media_height?: number;
  media_width?: number;
};

export type ChartContentJson = {
  link?: string;
  type: 'chart';
  hidden: boolean;
  transpose?: boolean;
  spec: ChartSpec;
  media_height?: number;
};

export type ChartLabel =
  | string
  | {
      icon: string;
      label: string;
    };

type ChartAxis = {
  allowDecimals?: boolean;
  visible?: boolean;
  title?: unknown;
  values?: ChartLabel[];
  axisFormat?: string;
  labels?: {
    style?: unknown;
  };
};

export type ChartSpec = {
  type: string;
  version: string;
  data: Record<string, unknown>[];
  options: Record<string, unknown> & {
    title?: {
      text?: string;
      size?: number;
    };
    xAxis?: ChartAxis | ChartAxis[];
    yAxis?: ChartAxis | ChartAxis[];
    legend?: {
      visible?: boolean;
      position?: string;
      itemStyle?: unknown;
    };
    highlight?: Record<string, number>[];
    animation: {
      disable: boolean;
    };
    width?: number;
    height?: number;
    exportingEnabled?: boolean;
  };
  prevalenceChart?: boolean;
};

export type VideoContentJson = {
  video: string;
  image?: string;
  present?: boolean;
  type: 'video' | 'mintel-video';
  hidden: boolean;
};

export type MediaContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    caption?: BlockElementJson;
    image?: ImageContentJson | ChartContentJson | SocialMediaContentJson;
  };
  name: 'media';
};

export type StatContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    [key: string]: BlockElementJson;
  };
  name: 'stat';
  background?: BlockBackgroundsType;
};

export type TableCellJson = {
  children?: React.ReactNode;
  content: string;
  header: boolean;
  colspan: number;
  rowspan: number;
  alignment?: Property.TextAlign;
};

export type TableContentJson = {
  data: TableCellJson[][];
  type: 'table';
  hidden: boolean;
};

export type TableContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    caption?: BlockElementJson;
    table: TableContentJson;
  };
  name: 'table';
};

export type QuoteContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    quote: HTMLContentJson;
    context?: HTMLContentJson;
  };
  name: 'quote';
  hidden: boolean;
  background?: BlockBackgroundsType;
};

export type QuadContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    title: HTMLContentJson;
    content: HTMLContentJson;
  };
  name: 'content';
  hidden: boolean;
};

export type VideoContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    video: VideoContentJson;
  };
  name: 'video';
  hidden: boolean;
};

export type ProductContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    content?: HTMLContentJson;
    image: ImageContentJson;
    title: HTMLContentJson;
  };
  name: 'product' | 'media';
  hidden: boolean;
};

export type RecommendedContainerJson = BlockElementJson & {
  type: 'container';
  elements: {
    date: string;
    image: string;
    title: string;
    source: string;
    url: string;
    contentType?: string;
    slug?: string;
  };
  name: 'recommended';
};

export type ColumnContainerJson =
  | AssetContainerJson
  | ContentContainerJson
  | MediaContainerJson
  | StatContainerJson
  | TableContainerJson
  | QuadContainerJson
  | QuoteContainerJson
  | VideoContainerJson
  | ProductContainerJson
  | RecommendedContainerJson;

export type ColumnsJson = {
  layout: 'equal' | 'left' | 'right';
  hidden: boolean;
  type: 'list';
  list: ColumnContainerJson[];
};

export type BlockContentJson = {
  border?: string;
  title?: HTMLContentJson;
  text?: BlockElementJson;
  columns?: ColumnsJson;
  context?: HTMLContentJson;
  footnote?: HTMLContentJson;
  image?: ImageContentJson;
  base?: Omit<ContentContainerJson, 'name'>;
  source?: Omit<ContentContainerJson, 'name'>;
};

export type BlockMetadata = BlockContentJson & {
  shareLink: string;
};

export type PresentOptionsJson = {
  animation?: { enabled: boolean };
  full_text?: boolean;
  selected_text?: boolean;
  hide?: boolean;
};

export type ArticleBlockJson = {
  id: string;
  type: string;
  json: string;
  html?: string;
  background_type: BlockBackgroundsType;
  background_id?: string;
  background_opacity: string;
  chart_specs?: { [id: string]: ChartSpec };
  linked_to_next: boolean;
  presentation_options?: PresentOptionsJson;
};

export type ProcessedBlock = Omit<ArticleBlockJson, 'json'> & {
  json: BlockContentJson;
};

export type ArticleSubscription = {
  salesforce_id: string;
  subscription_code: string;
  title: string;
};

export type ArticleJson = {
  id: string;
  content: ArticleBlockJson[];
  date: DateJson;
  editor: EditorJson;
  aggregated_insight_type: string;
  insight_type?: string[];
  leaf_category: { name: string[] };
  leaf_place: { name: string };
  language: string;
  platform: string[];
  site: string;
  slug: string;
  title: string;
  tags: string[];
  has_interactive_databook: boolean;
  interactive_databook?: string;
  protocol?: string;
  hostname?: string;
  mintel_editor_link?: string;
  highlight_terms: string[][];
  group_id: string;
  group_title?: string;
  article_image_url: string;
  resources?: Attachment[];
  has_executive_summary?: boolean;
  series?: string[];
  product: { name: string[]; id: string[] };
  primary_subscription?: ArticleSubscription;
  secondary_subscriptions?: ArticleSubscription[];
  migrated_from_oxygen: boolean;
  ai_translation?: boolean;
};

export type ArticleMetadata = Omit<ArticleJson, 'content'>;

export type Attachment = {
  id: string;
  mimetype: string;
  name: string;
  type: string;
};

/*
 * Props types
 *
 */

export type FavoriteMetadata = {
  id: string;
  title: string;
  url: string;
  article_image_url: string;
  group_id?: string;
};

export type Favorite = FavoriteMetadata & {
  timestamp: number;
  tag: string;
};

type IsFavoritedFunc = (id: string, groupId?: string) => boolean;
type OnFavoriteFunc = ({}: FavoriteMetadata) => void;
type OnDeleteFavoriteFunc = (id: string) => void;
type OnAddFavoriteFunc = (tag: string, favData: Favorite) => void;
type OnRenameFolderFunc = (prev: string, next: string) => void;
type OnDeleteFolderFunc = (tag: string) => void;

export type FavoritesPanelProps = {
  favorites: Favorite[];
  onDeleteFavorite: OnDeleteFavoriteFunc;
  onAddFavorite: OnAddFavoriteFunc;
  onRenameFolder: OnRenameFolderFunc;
  onDeleteFolder: OnDeleteFolderFunc;
};

export type FavoritesProps = {
  isFavorited: IsFavoritedFunc;
  onFavorite: OnFavoriteFunc;
  favoritesPanelProps: FavoritesPanelProps;
};

export type Mntl = {
  access: {
    clients?: {
      contacting?: number;
      downloads?: DownloadAccess;
      report_subscriptions?: string[];
      single_copy?: string[];
      verticals?: string[];
    };
    oxygen?: {
      accessible_codes?: string[];
      library_lockdown?: boolean;
    } & Record<string, boolean>;
    gnpd?: {
      disable_downloads?: number;
    };
  };
  fname: string;
  anon: number;
  flags: string[];
  mail: string;
  lang: string;
  org: string;
  is_academic: number;
};

export enum DownloadAccess {
  All = 'all-downloads',
  Limited = 'limited-downloads',
  None = 'no-downloads',
}

export type UserDownloadAccessRestrictions = {
  fullReport: boolean;
  myPresentation: boolean;
  executiveSummary: boolean;
  infographicOverview: boolean;
  assetBlock: boolean;
  chartData: boolean;
  tableData: boolean;
};

export type UserAccessRestrictions = {
  isAcademicUser: boolean;
  isAnonymousUser: boolean;
  isLibraryLockdownUser: boolean;
  isMasquerading: boolean;
  isLoggedInUser: boolean;
  toolsDisabled: boolean;
  downloads?: UserDownloadAccessRestrictions;
};

export type AppShellProps = Omit<
  ModuleComponentProps,
  'accessDetails' | 'startTracking' | 'emitter'
> & {
  startTracking: StartTrackingFn | false;
  accessDetails?: JwtPayload & {
    mntl?: Mntl;
  };
};

export type ContentLocation = {
  publishStatus: PublishStatus;
  contentType: string;
  slugOrId: string;
  blockNum?: string;
};

export type PublishStatus = 'live' | 'preview' | 'long_term_preview';

export type ArticleProps = {
  content: ArticleBlockJson[];
  ref: Ref<NullableHTMLDivElement[]>;
  onScreenBlock: MutableRefObject<string | undefined>;
  sendSnackbarEvent: SendSnackbarEventFn;
};

export type ArticleBlockProps = {
  blockId: string;
  blockIndex?: number;
  blockBackgroundImage?: string | null;
  blockBackgroundType: BlockBackgroundsType;
  blockBackgroundOpacity: string;
  blockContent: BlockContentJson;
  blockType: string;
  blockLinked: boolean;
  children?: JSX.Element;
  columns?: JSX.Element[];
  columnWidth?: number;
  contextContent?: string;
  offset?: number;
  rowCenterAlign?: boolean;
  presentCenterAlignDisabled?: boolean;
  titleWithinColumn?: boolean;
  noTitle?: boolean;
  titleCentered?: boolean;
  theme?: Theme;
  sub?: boolean;
  presentOption?: PresentOption;
  hideContext?: boolean;
  hideFooter?: boolean;
  sendSnackbarEvent?: SendSnackbarEventFn;
  blocksProjectData?: BlockProjectData[];
  tableDownloadRef?: RefObject<HTMLTableElement>;
};

export type BlockColumnsProps = {
  columnComponents: JSX.Element[];
  layout: 'equal' | 'left' | 'right';
  columnWidth?: number;
  rowCenterAlign?: boolean;
  presentCenterAlignDisabled?: boolean;
};

export type BlockAnalystProps = {
  editor: EditorJson;
  articleTitle: string;
};

export type BlockProps = ArticleBlockProps & {
  animationStep: number;
  animationEnabled: boolean;
  setAnimationComplete: (animationComplete: boolean) => void;
};

export type AccessListItem = {
  key: string;
  name: string;
  company: string;
};

export type CustomAccessType = {
  key: string;
  title: TranslatableString;
  accessItems: AccessListItem[];
};

export type CustomPanelType = {
  children?: JSX.Element;
  bridge?: { default: () => BridgeComponent<unknown> };
  closeSidebar: () => void;
  iconName: string;
  title: TranslatableString;
  subtitle?: TranslatableString;
  color?: string;
  url?: string;
  width: number;
  id: string;
};

export type SetIsLoading = (state: boolean) => void;

export type Section = {
  end: number;
  id: string;
  index: number;
  title: string;
  subsections?: Section[];
  sectionIndex?: number;
};

export type SeriesItem = {
  slug: string;
  title: string;
  series: string[];
  id: string;
  dateDisplay: Date;
};

export type SeriesSearchResponsePayload = Omit<SeriesItem, 'dateDisplay'> & {
  'date.display': string;
};

/*
 * API related types
 *
 */
export type ApiResponse = null | ArticleJson;

/*
 * Mintel User Tracking related types
 *
 */
type StartTrackingFn = (
  currentProduct: string,
  production: boolean,
) => MintelTracking;

export type MintelTracking = {
  engage: (location: string, properties: Record<string, unknown>) => void;
  download: (location: string, properties: Record<string, unknown>) => void;
  navigate: (properties: Record<string, unknown>) => void;
  view: (location: string, properties: Record<string, unknown>) => void;
  video: (
    eventName: 'Play' | 'Complete',
    properties: Record<string, unknown>,
  ) => void;
  manage: (location: string, properties: Record<string, unknown>) => void;
};

export type TrackRecommendedContentFn = (relatedTitle: string) => void;

export type TrackPresentModeFn = (
  fulltext: boolean,
  user: string,
  width: number,
  height: number,
) => void;

export type TrackPresentPanelDownloadFn = () => void;

/*
 * Misc types
 *
 */
export type HandlePresentType = (fulltext?: boolean) => void;

export enum Present {
  Off = '', // must be falsy string
  Full = 'full-written',
  Keynote = 'keynote',
}

export enum PresentOption {
  Default = '', // must be falsy string
  Full = 'full-text',
  Selected = 'selected-text',
}

export enum BlockBackgrounds {
  Black = 'bg-black',
  Grey = 'bg-grey',
  White = 'bg-white',
  Image = 'bg-image',
  Grad01 = 'grad01',
  Grad02 = 'grad02',
  Grad03 = 'grad03',
  Grad04 = 'grad04',
  Grad05 = 'grad05',
  GradPicture01 = 'grad01-picture',
  GradPicture02 = 'grad02-picture',
  GradPicture03 = 'grad03-picture',
  GradPicture04 = 'grad04-picture',
  GradPicture05 = 'grad05-picture',
}

export type BlockBackgroundsType = `${BlockBackgrounds}`;

export type Theme = 'default' | 'compere';

export type ChromaticParameters = {
  disableSnapshot?: boolean;
};

export type NullableHTMLDivElement = HTMLDivElement | null;
export type TranslatableString = React.ReactElement<typeof Trans>;

export type SnackbarEvent = {
  key: string;
  message: string;
  type?: string;
};

export type SendSnackbarEventFn = (message: string, type?: string) => void;

export enum FeatureFlagState {
  TRUE,
  FALSE,
  LOADING,
}

export type BlockProjectData = {
  item_uuid: string;
  title: string;
  version: string;
  product: string;
  slide_type: string;
};

export type TranslationResult = {
  id: string;
  'date.display': string;
  language: string;
  title: string;
  absolute_url: string;
  is_current: boolean;
};

export type TranslationResults = TranslationResult[] | [];

export type PublicationAlertsResults = {
  publication_alerts: PublicationAlertType[];
};

export type PublicationAlertType = {
  description: string;
  absoluteUrl: string;
  language: string;
  displayDate: Date;
  published: string;
  id: number;
  frequency: number;
  articleId: string;
};

export type SearchAlertsResults = SearchAlertType[] | [];

export type SearchAlertType = {
  id: number;
  frequency: number;
  description: string;
  searchDomain: string;
  searchArgs: string;
};

export type AccountManager = {
  account_info: {
    email: string;
    message: string;
    name?: string;
    photo: string;
    title: string;
  };
};
